<script setup lang="ts">
import { computed } from 'vue';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { playerTypeIcon } from '@/composables/modelIcons';
import { PLAYER_TYPES } from '@/composables/playerTypeGlyphs';

/**
 * Player-type glyph for Location Zone Player rows, with a tooltip naming the
 * type. The glyph is the player's own icon from modelIcons, so it draws in the
 * player colour here as it does in search results and relation links. An
 * unknown/absent type renders nothing at all.
 *
 * The brand colour belongs to the zone's star (SAU-514): a zone is TPG when
 * `is_tpg_location_zone` is set, and modelIcons colours its mark. The brand
 * still names itself in the label and in data-brand: 'grayv', 'tpg', or
 * "unknown" for an absent or unrecognized value.
 */
const props = defineProps<{
    playerTypeId: number | null | undefined;
    /** Server-computed brand; absent or unrecognized values render as unknown. */
    brand?: string | null;
}>();

const BRAND_DISPLAY_NAMES: Record<'grayv' | 'tpg', string> = {
    grayv: 'Gray V',
    tpg: 'TPG',
};

const playerType = computed(() =>
    typeof props.playerTypeId === 'number' ? (PLAYER_TYPES[props.playerTypeId] ?? null) : null
);

const icon = computed(() => playerTypeIcon(props.playerTypeId));

const normalizedBrand = computed<'grayv' | 'tpg' | 'unknown'>(() =>
    props.brand === 'grayv' || props.brand === 'tpg' ? props.brand : 'unknown'
);

const label = computed(() => {
    if (!playerType.value) {
        return '';
    }
    const brandName = normalizedBrand.value === 'unknown' ? null : BRAND_DISPLAY_NAMES[normalizedBrand.value];
    return brandName ? `${playerType.value.label} — ${brandName}` : playerType.value.label;
});
</script>

<template>
    <Tooltip v-if="playerType && icon">
        <TooltipTrigger as-child>
            <span
                class="inline-block size-4 shrink-0"
                role="img"
                :aria-label="label"
                data-testid="player-type-icon"
                :data-player-type="playerType.key"
                :data-brand="normalizedBrand"
            >
                <component :is="icon" class="size-full" aria-hidden="true" />
            </span>
        </TooltipTrigger>
        <TooltipContent side="top" align="start">{{ label }}</TooltipContent>
    </Tooltip>
</template>
